/**
 * Org Field Configuration — Admin page for managing custom policy fields.
 * Templates group field definitions. Definitions describe individual custom fields.
 */
import React, { useState, useEffect, useCallback } from 'react';
import InfoTooltip from '../../components/InfoTooltip';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL;

const FIELD_TYPES = [
  { value: 'text',         label: 'Text' },
  { value: 'textarea',     label: 'Text Area' },
  { value: 'number',       label: 'Number' },
  { value: 'date',         label: 'Date' },
  { value: 'select',       label: 'Dropdown' },
  { value: 'multi_select', label: 'Multi-Select' },
  { value: 'boolean',      label: 'Yes/No' },
];

export default function OrgFieldConfig() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [definitions, setDefinitions] = useState([]);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [showCreateField, setShowCreateField] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [fieldForm, setFieldForm] = useState({ field_key: '', display_label: '', field_type: 'text', required: false, options: '', display_order: 0, filterable: false, populate_from_upload: false, extraction_hint: '' });
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, display_label, field_key } or null
  const [deleting, setDeleting] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, dRes] = await Promise.all([
        fetch(`${API}/api/ppm/org-fields/templates`, { credentials: 'include' }),
        fetch(`${API}/api/ppm/org-fields/definitions`, { credentials: 'include' }),
      ]);
      const tData = await tRes.json();
      const dData = await dRes.json();
      if (tData.success) setTemplates(tData.templates || []);
      if (dData.success) setDefinitions(dData.definitions || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const toggleFlag = async (defId, flag, value) => {
    try {
      await fetch(`${API}/api/ppm/org-fields/definitions/${defId}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [flag]: value }),
      });
      setDefinitions(prev => prev.map(d => d.id === defId ? { ...d, [flag]: value } : d));
    } catch {}
  };

  const requestDelete = (def) => setConfirmDelete(def);

  const performDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/ppm/org-fields/definitions/${confirmDelete.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        setDefinitions(prev => prev.filter(d => d.id !== confirmDelete.id));
        setConfirmDelete(null);
      } else {
        setError(data.message || 'Delete failed — only a Compliance Officer can remove fields.');
        setConfirmDelete(null);
      }
    } catch {
      setError('Delete failed — check your connection and try again.');
      setConfirmDelete(null);
    }
    setDeleting(false);
  };

  const handleCreateTemplate = async () => {
    if (!templateName.trim()) return;
    setError('');
    try {
      const res = await fetch(`${API}/api/ppm/org-fields/templates`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: templateName.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setTemplateName('');
        setShowCreateTemplate(false);
        loadTemplates();
        setSelectedTemplate(data.template);
      } else setError(data.message);
    } catch { setError('Failed to create template'); }
  };

  const handleCreateField = async () => {
    if (!selectedTemplate || !fieldForm.field_key.trim() || !fieldForm.display_label.trim()) {
      setError('Field key and label are required'); return;
    }
    setError('');
    try {
      const body = {
        template_id: selectedTemplate.id,
        field_key: fieldForm.field_key.trim().toLowerCase().replace(/\s+/g, '_'),
        display_label: fieldForm.display_label.trim(),
        field_type: fieldForm.field_type,
        required: fieldForm.required,
        display_order: fieldForm.display_order,
        filterable: fieldForm.filterable,
        populate_from_upload: fieldForm.populate_from_upload,
        extraction_hint: fieldForm.extraction_hint || null,
      };
      if (['select', 'multi_select'].includes(fieldForm.field_type) && fieldForm.options.trim()) {
        body.options = fieldForm.options.split(',').map(o => o.trim()).filter(Boolean);
      }
      const res = await fetch(`${API}/api/ppm/org-fields/definitions`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setFieldForm({ field_key: '', display_label: '', field_type: 'text', required: false, options: '', display_order: 0, filterable: false, populate_from_upload: false, extraction_hint: '' });
        setShowCreateField(false);
        setDefinitions(prev => [...prev, data.definition]);
      } else setError(data.message);
    } catch { setError('Failed to create field'); }
  };

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-800 tracking-tight">Custom Policy Fields</h2>
              <InfoTooltip
                title="Field Config"
                width={380}
                content={
                  <>
                    Controls which columns appear in the Policy Library and which fields
                    show up in the upload form. Fields added here are the ones the AI
                    will extract from uploaded PDFs/DOCX. Only a
                    {' '}<span className="text-white font-medium">Compliance Officer</span>{' '}
                    can remove a field, and removal is permanent — values are wiped from
                    every policy.
                  </>
                }
              />
            </div>
            <p className="text-slate-400 text-xs mt-0.5">Configure org-specific metadata fields for policies</p>
          </div>
          <button onClick={() => setShowCreateTemplate(true)}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity">
            + New Template
          </button>
        </div>

        {error && <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">{error}</div>}

        {/* Create Template Form */}
        {showCreateTemplate && (
          <div className="mb-6 bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Create Field Template</h3>
            <div className="flex gap-3">
              <input type="text" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="e.g., Medicare Contract Fields"
                className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
              <button onClick={handleCreateTemplate}
                className="px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors">Create</button>
              <button onClick={() => setShowCreateTemplate(false)}
                className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 transition-colors">Cancel</button>
            </div>
          </div>
        )}

        {/* Templates List */}
        {loading && <div className="text-slate-400 text-sm py-8">Loading...</div>}

        {!loading && templates.length === 0 && !showCreateTemplate && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl flex items-center justify-center text-blue-600">
              <Cog6ToothIcon className="w-8 h-8" strokeWidth={1.75} />
            </div>
            <h3 className="text-base font-semibold text-slate-700 mb-1">No field templates</h3>
            <p className="text-sm text-slate-400">Create a template to define custom metadata fields for your org's policies.</p>
          </div>
        )}

        {!loading && templates.length > 0 && (
          <div className="space-y-3">
            {templates.map(t => (
              <div key={t.id} className={`bg-white rounded-2xl border p-5 transition-all cursor-pointer ${
                selectedTemplate?.id === t.id ? 'border-blue-300 bg-blue-50/30' : 'border-slate-200 hover:border-slate-300'
              }`} onClick={() => { setSelectedTemplate(t); setDefinitions([]); }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">{t.name}</h3>
                    <p className="text-xs text-slate-400 mt-0.5">{t.field_count || 0} field(s) · version {t.version}</p>
                  </div>
                  {selectedTemplate?.id === t.id && (
                    <button onClick={(e) => { e.stopPropagation(); setShowCreateField(true); }}
                      className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-colors">
                      + Add Field
                    </button>
                  )}
                </div>

                {/* Field definitions (when selected) */}
                {selectedTemplate?.id === t.id && (() => {
                  const tdefs = definitions.filter(d => d.template_id === t.id);
                  if (tdefs.length === 0) return null;
                  return (
                    <div className="mt-4 space-y-2">
                      {tdefs.map(d => (
                        <div key={d.id} className="bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-xs font-mono text-slate-500">{d.field_key}</span>
                            <span className="text-sm text-slate-700 font-medium">{d.display_label}</span>
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-500 border border-slate-200 uppercase">{d.field_type}</span>
                            {d.required && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200">Required</span>}
                            <button onClick={(e) => { e.stopPropagation(); requestDelete(d); }}
                              className="ml-auto text-xs text-red-600 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded-md font-medium transition-colors">
                              Remove
                            </button>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs">
                            <label className="flex items-center gap-1.5 cursor-pointer"
                              onClick={e => e.stopPropagation()}>
                              <input type="checkbox" checked={!!d.populate_from_upload}
                                onChange={e => toggleFlag(d.id, 'populate_from_upload', e.target.checked)}
                                className="rounded border-slate-300" />
                              <span className="text-slate-600">Auto-populate from uploads</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer"
                              onClick={e => e.stopPropagation()}>
                              <input type="checkbox" checked={!!d.filterable}
                                onChange={e => toggleFlag(d.id, 'filterable', e.target.checked)}
                                className="rounded border-slate-300" />
                              <span className="text-slate-600">Filterable in library</span>
                            </label>
                          </div>
                          {d.extraction_hint && (
                            <div className="mt-1 text-[11px] text-slate-400 italic">AI hint: {d.extraction_hint}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Add Field Form */}
                {selectedTemplate?.id === t.id && showCreateField && (
                  <div className="mt-4 bg-slate-50 rounded-xl border border-slate-200 p-4">
                    <h4 className="text-xs font-semibold text-slate-600 mb-3">Add Field</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Field Key *</label>
                        <input type="text" value={fieldForm.field_key} onChange={e => setFieldForm(f => ({ ...f, field_key: e.target.value }))} placeholder="medicare_contract_number"
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Display Label *</label>
                        <input type="text" value={fieldForm.display_label} onChange={e => setFieldForm(f => ({ ...f, display_label: e.target.value }))} placeholder="Medicare Contract Number"
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Field Type</label>
                        <select value={fieldForm.field_type} onChange={e => setFieldForm(f => ({ ...f, field_type: e.target.value }))}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500">
                          {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                        </select>
                      </div>
                      <div className="flex items-end gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={fieldForm.required} onChange={e => setFieldForm(f => ({ ...f, required: e.target.checked }))} className="rounded border-slate-300" />
                          <span className="text-sm text-slate-600">Required</span>
                        </label>
                      </div>
                      {['select', 'multi_select'].includes(fieldForm.field_type) && (
                        <div className="col-span-2">
                          <label className="block text-xs text-slate-500 mb-1">Options (comma-separated)</label>
                          <input type="text" value={fieldForm.options} onChange={e => setFieldForm(f => ({ ...f, options: e.target.value }))} placeholder="Option A, Option B, Option C"
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
                        </div>
                      )}
                      <div className="col-span-2 flex gap-4 items-center">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={fieldForm.populate_from_upload}
                            onChange={e => setFieldForm(f => ({ ...f, populate_from_upload: e.target.checked }))}
                            className="rounded border-slate-300" />
                          <span className="text-sm text-slate-600">Auto-populate from policy uploads (AI extracts from document)</span>
                        </label>
                      </div>
                      <div className="col-span-2 flex gap-4 items-center">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={fieldForm.filterable}
                            onChange={e => setFieldForm(f => ({ ...f, filterable: e.target.checked }))}
                            className="rounded border-slate-300" />
                          <span className="text-sm text-slate-600">Show as filter in Policy Library</span>
                        </label>
                      </div>
                      {fieldForm.populate_from_upload && (
                        <div className="col-span-2">
                          <label className="block text-xs text-slate-500 mb-1">AI extraction hint (optional)</label>
                          <input type="text" value={fieldForm.extraction_hint}
                            onChange={e => setFieldForm(f => ({ ...f, extraction_hint: e.target.value }))}
                            placeholder="e.g., Look for 'Plan Year' in the policy header or metadata block"
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
                          <p className="text-[10px] text-slate-400 mt-1">Guides the AI when reading uploaded documents to find this field.</p>
                        </div>
                      )}
                      <div className="col-span-2 flex gap-3 pt-1">
                        <button onClick={handleCreateField}
                          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-colors">Add Field</button>
                        <button onClick={() => setShowCreateField(false)}
                          className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-xs hover:bg-slate-50 transition-colors">Cancel</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirm delete modal — hard delete, only compliance_officer can confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
          onClick={() => !deleting && setConfirmDelete(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
          >
            <div className="px-6 pt-6 pb-4 flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-slate-900">
                  Remove field "{confirmDelete.display_label}"?
                </h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                  This will <span className="font-semibold text-slate-900">permanently delete</span> the field from Field Config, the Policy Library, and the upload form.
                  All values for this field will be <span className="font-semibold text-slate-900">wiped from every policy</span> in your org. This cannot be undone.
                </p>
                <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  Only a <span className="font-semibold">Compliance Officer</span> can remove fields. If you're not authorized, the request will be denied by the server.
                </div>
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3 justify-end">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
                className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={performDelete}
                disabled={deleting}
                className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Removing…' : 'Remove field permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
